import React, { memo, useContext, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { FormField, FormHelp, Label, Select } from '@zillow/constellation-10';

import WizardServerError from '../../../model/WizardServerError';

import { DISPLAY_SERVER_ERROR_AT } from '../../../constants/WizardConstants';

import { ModularListingContext } from '../../../containers/CreateListingPage/context';
import { WizardContext } from '../../Wizard/context';
import { AppSelectors } from '../../../selectors';
import { useForm } from '../../../hooks';

import { multiFieldsUtil, UPDATE_CURRENT_PROPERTY_KEY } from '../../../utils/postingUtils';
import { postingConstraints } from '../../../utils/formValidation';

import actions from '../../../containers/CreateListingPage/actions';
import wizardActions from '../../Wizard/actions';
import PropertiesActions from '../../../actions/PropertiesActions';

const propTypes = {
  initialValue: PropTypes.string,
  inputName: PropTypes.string.isRequired,
  label: PropTypes.string,
  optionsList: PropTypes.array.isRequired,
  // the parent key where the data will be saved to via multi endpoint
  saveToParentKey: PropTypes.string,
  // the key that determines how to update current property
  updateCurrentPropertyKey: PropTypes.string,
};

const defaultProps = {
  label: '',
  initialValue: '',
  saveToParentKey: null,
  updateCurrentPropertyKey: null,
};

const DropdownFormField = ({
  initialValue,
  inputName,
  label,
  optionsList,
  saveToParentKey,
  updateCurrentPropertyKey,
}) => {
  const dispatch = useDispatch();

  const { reducerWizardDispatch } = useContext(WizardContext);

  const { listingAlias, roomForRent: { currentRoommatesDetails: { roommateTotalCount = null } } = {} } = useSelector(
    (state) => state.properties.currentProperty,
  );
  const isScreenDesktop = useSelector((state) => AppSelectors.makeIsScreenDesktop(state));

  const {
    modularListingState: { isInModalState, isSaveComplete, saveOutsideComponent, serverErrorObj, shouldSaveOnBlur },
    reducerDispatch,
  } = useContext(ModularListingContext);

  const prevValue = useRef();

  const {
    inputs,
    errorObj,
    hasFieldError,
    handleChange: handleFormChange,
  } = useForm(
    {
      [inputName]: initialValue,
    },
    postingConstraints,
  );

  useEffect(() => {
    if (isSaveComplete) {
      // eslint-disable-next-line no-use-before-define
      save({
        target: {
          name: inputName,
          value: inputs[inputName],
        },
      });
    }
  }, [isSaveComplete]);

  useEffect(() => {
    updatePrevValueRef(initialValue);
  }, []);

  const clearServerError = () => {
    if (serverErrorObj.fieldName) {
      actions.removeServerError({
        reducerDispatch,
      });
    }
  };

  const save = (event) => {
    // return if user must click on cta
    if (!shouldSaveOnBlur && !isSaveComplete) {
      return;
    }

    const { value, name: fieldName } = event.target;

    const isValueChanging = value !== prevValue.current;

    if (hasFieldError(fieldName, value)) {
      actions.updateHasFieldError({ reducerDispatch, hasFieldError: true });
      actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });

      return;
    }

    if (isValueChanging === false && saveOutsideComponent === false) {
      actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });
      return;
    }

    // saveOutsideComponent check must be above updateIsFetchingData to keep next and prev functionality
    if (saveOutsideComponent) {
      actions.addFieldDataEntry({ reducerDispatch, entryObj: { saveToParentKey, fieldName, value } });

      return;
    }

    const dataToSave = multiFieldsUtil.getDataToSave({
      listingAlias,
      saveToParentKey,
      fieldName,
      value,
    });

    wizardActions.updateIsFetchingData({ reducerWizardDispatch, isFetchingData: true });

    dispatch(PropertiesActions.saveMultipleFieldsPropertyInfo(dataToSave))
      .then(() => {
        updateCurrentProperty({ value });

        updatePrevValueRef(value);

        // reset save cta
        actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });
      })
      .catch((message) => {
        updateServerError(message, fieldName);
      })
      .finally(() => {
        wizardActions.updateIsFetchingData({
          reducerWizardDispatch,
          isFetchingData: false,
        });
      });
  };

  const updateCurrentProperty = ({ value }) => {
    if (updateCurrentPropertyKey && updateCurrentPropertyKey === UPDATE_CURRENT_PROPERTY_KEY.CURRENT_PETS) {
      dispatch(
        PropertiesActions.updateCurrentPropertyCurrentPets({
          [inputName]: value,
        }),
      );

      return;
    }

    dispatch(
      PropertiesActions.updateCurrentPropertyCurrentRoommatesDetails({
        [inputName]: value,
      }),
    );
  };

  const updatePrevValueRef = (value) => {
    prevValue.current = value;
  };

  const updateServerError = (message = '', fieldName = '') => {
    const displayAt = isInModalState ? DISPLAY_SERVER_ERROR_AT.FIELD : DISPLAY_SERVER_ERROR_AT.PAGE;

    actions.updateServerError({
      reducerDispatch,
      serverErrorObj: WizardServerError({
        displayAt,
        fieldName,
        message,
        displayMessage: message,
      }),
    });
  };

  const handleChange = (event) => {
    const { value: pValue, name } = event.target;
    let value = pValue;

    if (value === 'Select Option') {
      value = '';
    }

    const mockEvent = {
      target: {
        name,
        value,
      },
    };

    clearServerError();
    handleFormChange(mockEvent);
  };

  return (
    <FormField
      className="DropdownFormField"
      label={<Label required={false}>{label}</Label>}
      control={
        <Select
          size={isScreenDesktop ? 'lg' : 'md'}
          aria-label={label}
          name={inputName}
          value={inputs[inputName]}
          min="0"
          onChange={handleChange}
          onBlur={save}
          error={!!errorObj[inputName]}
          required={false}
        >
          {optionsList.map(({ label, value }) => {
            return (
              <option key={value} value={value}>
                {label}
              </option>
            );
          })}
        </Select>
      }
      description={<FormHelp error={errorObj.roommateTotalCount}>{errorObj.roommateTotalCount}</FormHelp>}
    />
  );
};

DropdownFormField.propTypes = propTypes;
DropdownFormField.defaultProps = defaultProps;

export default memo(DropdownFormField);
